<template>
  <!-- Page Header -->
  <PageHeader
    title="Gestion des upload photos avancées"
    subtitle="Gérez vos uploads de photos avec catégories et modèles"
    class="mb-0 pr-10"
  >
    <template #extra>
      <div class="desktop-upload-actions items-center gap-2">
        <button
          type="button"
          class="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
          :class="
            activeUploadMode === 'zip'
              ? 'ring-2 ring-blue-300 ring-offset-2'
              : ''
          "
          :aria-expanded="activeUploadMode === 'zip'"
          aria-controls="desktop-upload-modes"
          @click="toggleUploadMode('zip')"
        >
          <i class="fas fa-file-archive mr-2"></i>
          Upload groupé
        </button>
      </div>
    </template>
  </PageHeader>

  <div class="w-full px-2 sm:px-6 py-4">
    <!-- Bandeau informatif si shootingId présent -->
    <div
      v-if="shootingIdsFromUrl?.length"
      class="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <i class="fas fa-camera text-blue-600 text-xl"></i>
          <div>
            <h3 class="text-sm font-semibold text-blue-900">
              Uploads pour
              {{
                shootingIdsFromUrl.length === 1
                  ? "le shooting"
                  : "les shootings"
              }}
              #{{ shootingIdsFromUrl.join(", #") }}
            </h3>
            <p class="text-xs text-blue-700 mt-1">
              Vous visualisez uniquement les uploads associés à
              {{
                shootingIdsFromUrl.length === 1
                  ? "ce shooting"
                  : "ces shootings"
              }}
            </p>
          </div>
        </div>
        <button
          @click="handleShowAll"
          class="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          <i class="fas fa-list mr-1"></i>
          Voir tous les uploads
        </button>
      </div>
    </div>

    <div
      class="mb-6 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r"
      role="alert"
    >
      <div class="flex items-start gap-3">
        <i
          class="fas fa-exclamation-triangle text-amber-600 text-xl mt-0.5"
        ></i>
        <div>
          <h3 class="text-sm font-semibold text-amber-900">
            Important — Photos générées par IA
          </h3>
          <p class="text-xs text-amber-800 mt-1">
            Si vous utilisez des photos générées par intelligence artificielle,
            veuillez également ajouter au moins une photo réelle du produit sur
            cintre, non générée par IA.
          </p>
        </div>
      </div>
    </div>

    <!-- Filtres Section -->
    <UploadProductFilters
      :total-produits="totalItems"
      :categories="categoryOptions"
      :sous-categories="sousCategoryOptions"
      :sous-sous-categories="sousSousCategoryOptions"
      :categories-tree="categoriesTree"
      :initial-filters="currentFilters"
      @filter="handleFilter"
      @show-all="handleShowAll"
    />

    <div
      v-show="activeUploadMode"
      id="desktop-upload-modes"
      class="upload-mode-grid mb-6 grid grid-cols-1 gap-4 items-start"
    >
      <!-- Upload groupé (ZIP) : max 100 Mo, fichiers à plat dans l’archive, noms {reference-couleur}-{position}.ext -->
      <div
        v-show="activeUploadMode === 'zip'"
        class="upload-mode-panel mb-0 p-4 bg-white rounded-xl border border-gray-200 shadow-sm h-full"
      >
        <h3 class="text-sm font-semibold text-black mb-2">
          Upload groupé (ZIP)
        </h3>
        <p class="text-xs text-black mb-3">
          Déposez un ZIP (max 100 Mo) : Nom des fichiers :
          <strong>{reference-couleur}-{position}</strong>, ex.
          <code class="bg-gray-100 px-1 rounded">{{ "REF-NOIR-0.jpg" }}</code
          >. Max 20 photos par référence.
        </p>
        <div
          :class="[
            'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
            isZipUploading
              ? 'border-gray-300 bg-gray-50 cursor-wait'
              : zipDragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer',
          ]"
          @click="!isZipUploading && triggerZipInput()"
          @dragover.prevent="zipDragOver = true"
          @dragleave.prevent="zipDragOver = false"
          @drop.prevent="handleZipDrop"
        >
          <input
            ref="zipFileInputRef"
            type="file"
            accept=".zip"
            class="hidden"
            @change="handleZipFileChange"
          />
          <i
            class="fas fa-file-archive text-3xl mb-2"
            :class="isZipUploading ? 'text-black' : 'text-blue-500'"
          ></i>
          <p class="text-sm text-black">
            {{
              selectedZipFile
                ? selectedZipFile.name
                : "Glissez-déposez un fichier ZIP ou cliquez pour sélectionner"
            }}
          </p>
          <p class="text-xs text-black mt-1">Taille max : 100 Mo</p>
          <div class="mt-3 flex justify-center gap-2">
            <button
              type="button"
              :disabled="!selectedZipFile || isZipUploading"
              class="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              @click.stop="submitZipUpload"
            >
              <span
                v-if="isZipUploading"
                class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 align-middle"
              ></span>
              {{ isZipUploading ? "Traitement en cours..." : "Envoyer le ZIP" }}
            </button>
          </div>
        </div>
        <!-- Message général (erreur réseau, ZIP invalide, etc.) -->
        <p v-if="zipErrorMessage" class="mt-2 text-sm text-red-600">
          {{ zipErrorMessage }}
        </p>
        <!-- Bloc d'erreurs détaillées (comme demande de mise en ligne Excel) : ref erronées, fichiers invalides… les autres refs sont passées -->
        <div
          v-if="zipErrors.length > 0"
          class="mt-3 bg-red-50 border border-red-200 rounded-lg"
        >
          <button
            type="button"
            class="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-red-100 transition-colors rounded-lg"
            @click="showZipErrors = !showZipErrors"
          >
            <span class="font-medium text-red-800">
              <i class="fas fa-exclamation-circle mr-2"></i>
              Erreurs dans le ZIP ({{ zipErrors.length }})
            </span>
            <i
              :class="[
                'fas transition-transform text-red-600',
                showZipErrors ? 'fa-chevron-up' : 'fa-chevron-down',
              ]"
            ></i>
          </button>
          <div v-show="showZipErrors" class="px-4 pb-4">
            <p class="text-xs text-red-700 mb-2">
              Les références valides ont été traitées. Corrigez les erreurs
              ci-dessous pour les autres.
            </p>
            <ul class="list-disc list-inside text-sm text-red-700 space-y-1">
              <li v-for="(err, i) in zipErrors" :key="i">
                {{ err }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-inline">
      <UnifiedPagination
        :total-items="totalItems"
        :current-page="currentPage"
        :items-per-page="itemsPerPage"
        variant="product"
        item-label="produits"
        :items-per-page-options="[10, 25, 50, 100]"
        :show-first-last-buttons="true"
        @update:current-page="uploadPhotoStore.setPage($event)"
        @update:items-per-page="uploadPhotoStore.setItemsPerPage($event)"
      />
    </div>

    <!-- Indicateur de chargement -->
    <div
      v-if="isLoading"
      class="bg-white mt-3 rounded-xl shadow-sm border border-gray-200 p-12 text-center"
    >
      <div class="flex flex-col items-center justify-center">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"
        ></div>
        <p class="text-black text-lg font-medium">
          Chargement des uploads en cours...
        </p>
        <p class="text-black text-sm mt-2">Veuillez patienter</p>
      </div>
    </div>

    <!-- Tableau des uploads -->
    <div v-else class="mt-3">
      <!-- Desktop: tableau original -->
      <div
        class="hidden tablet:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-3 py-2 text-left text-xs font-medium text-black uppercase tracking-wider"
                >
                  Date de création
                </th>
                <th
                  class="px-3 py-2 text-left text-xs font-medium text-black uppercase tracking-wider"
                >
                  RÉFÉRENCE
                </th>
                <th
                  class="px-3 py-2 text-left text-xs font-medium text-black uppercase tracking-wider"
                >
                  Data
                </th>
                <th
                  class="px-3 py-2 text-left text-xs font-medium text-black uppercase tracking-wider"
                >
                  Upload
                </th>
                <th
                  class="px-3 py-2 text-left text-xs font-medium text-black uppercase tracking-wider"
                >
                  Aperçu
                </th>
                <th
                  class="px-3 py-2 text-left text-xs font-medium text-black uppercase tracking-wider"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="upload in paginatedUploads"
                :key="upload.id"
                class="hover:bg-gray-50"
              >
                <!-- 1. Date de création -->
                <td
                  class="px-3 py-2 whitespace-nowrap text-sm text-black font-medium align-top"
                >
                  {{ formatDate(upload.dateCreation) }}
                </td>

                <!-- 2. Référence -->
                <td class="px-3 py-2 align-top max-w-xs">
                  <div class="flex flex-col gap-1">
                    <button
                      type="button"
                      class="text-left text-blue-600 hover:text-blue-800 font-medium text-sm break-words"
                      @click="openProductSheetFromUpload(upload)"
                    >
                      {{ upload.reference }}
                    </button>
                    <span
                      v-if="upload.isMain"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-600 text-white whitespace-nowrap w-fit"
                    >
                      Principale
                    </span>
                    <span
                      v-if="upload.premel === '1'"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 whitespace-nowrap w-fit"
                    >
                      Brouillon
                    </span>
                  </div>
                </td>

                <!-- 3. Data -->
                <td class="px-3 py-2 align-top">
                  <div class="text-xs space-y-1">
                    <div class="font-medium text-black">
                      {{ upload.categories.general }}
                    </div>
                    <div class="text-black">
                      {{ upload.categories.type }} ›
                      {{ upload.categories.sousType }}
                    </div>
                    <div class="text-black">
                      {{ getCollectionLabel(upload.collection) }}
                    </div>
                  </div>
                </td>

                <!-- 4. Upload -->
                <td class="px-3 py-2 whitespace-nowrap align-top">
                  <div
                    :class="[
                      'border-2 border-dashed rounded-lg p-3 text-center transition-all duration-200',
                      canEditUpload(upload)
                        ? isDragOver(upload.id)
                          ? 'border-blue-500 bg-blue-50 shadow-md cursor-grabbing'
                          : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer bg-white'
                        : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60',
                    ]"
                    @click="triggerFileDialog(upload)"
                    @dragenter.prevent="handleDragEnter($event, upload.id)"
                    @dragover.prevent="handleDragOver($event, upload.id)"
                    @dragleave.prevent="handleDragLeave($event, upload.id)"
                    @drop.prevent="handleDrop($event, upload)"
                  >
                    <div
                      class="flex flex-col items-center gap-2 justify-center min-h-[60px]"
                    >
                      <i
                        class="fas fa-cloud-upload-alt text-2xl"
                        :class="
                          canEditUpload(upload)
                            ? isDragOver(upload.id)
                              ? 'text-blue-500'
                              : 'text-black'
                            : 'text-black'
                        "
                      ></i>

                      <template v-if="canEditUpload(upload)">
                        <span
                          v-if="isUploadingPhotos(upload.id)"
                          class="flex items-center gap-2 text-xs text-blue-600 font-medium"
                        >
                          <span
                            class="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
                          ></span>
                          Téléversement...
                        </span>
                        <span v-else class="text-xs font-medium text-black">
                          {{
                            isDragOver(upload.id)
                              ? "Déposez ici"
                              : "Glissez ou cliquez"
                          }}
                        </span>
                      </template>
                      <span v-else class="text-xs text-black"
                        >Indisponible</span
                      >

                      <span
                        v-if="(upload.nbPhotos ?? upload.images.length) > 0"
                        class="text-[10px] font-medium text-black"
                      >
                        {{ upload.nbPhotos ?? upload.images.length }} photo(s)
                      </span>
                    </div>
                  </div>
                </td>

                <!-- 5. Aperçu -->
                <td class="px-3 py-2 align-top">
                  <div class="flex flex-wrap gap-2 max-w-[250px]">
                    <!-- <div v-for="(image, index) in upload.images" :key="index" class="relative w-20 h-20 group">
                      <img :src="getPreviewPhotoUrl(image)" class="w-full h-full object-cover rounded border" />
                      <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-1 transition-opacity">
                        <button @click.stop="handleDeletePhoto(upload, image)" class="text-white bg-red-500 p-1 rounded-full text-xs">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div> -->
                    <div
                      v-for="(image, index) in upload.images"
                      :key="`${upload.id}-${index}-${getPhotoPosition(
                        image,
                        upload.id,
                      )}`"
                      class="flex flex-col items-center gap-1.5"
                    >
                      <div
                        :draggable="canEditUpload(upload)"
                        :class="[
                          'relative group w-28 h-28 bg-gray-100 rounded overflow-hidden flex items-center justify-center cursor-move transition-all',
                          isMainPhoto(image, upload.id)
                            ? 'border-2 border-blue-500 shadow-md'
                            : 'border border-gray-300',
                          isDraggingPhoto(upload.id, index)
                            ? 'opacity-50 scale-95'
                            : '',
                          isDragOverPhoto(upload.id, index)
                            ? 'ring-2 ring-blue-400 ring-offset-1'
                            : '',
                        ]"
                        @click="togglePhotoOverlay(upload.id, index)"
                        @dragstart="handlePhotoDragStart($event, upload, index)"
                        @dragend="handlePhotoDragEnd(upload.id, index)"
                        @dragover.prevent="
                          handlePhotoItemDragOver($event, upload.id, index)
                        "
                        @dragleave.prevent="handlePhotoItemDragLeave()"
                        @drop.prevent="
                          handlePhotoDropOnItem($event, upload, index)
                        "
                      >
                        <img
                          :src="getPreviewPhotoUrl(image)"
                          :alt="`Photo ${index + 1}`"
                          loading="lazy"
                          class="max-w-full max-h-full object-contain rounded pointer-events-none"
                          @error="(event: Event) => { const target = event.target as HTMLImageElement; if (target) target.style.display = 'none'; }"
                        />
                        <!-- Badge "Photo de couverture" dans la photo -->
                        <div
                          v-if="isMainPhoto(image, upload.id)"
                          class="absolute top-0 left-0 right-0 bg-blue-500 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-b text-center leading-tight"
                        >
                          Photo de<br />couverture
                        </div>
                        <!-- Icône de drag -->
                        <div
                          v-if="canEditUpload(upload)"
                          class="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 pointer-events-none"
                        >
                          <i class="fas fa-grip-vertical text-[8px]"></i>
                        </div>
                        <div
                          v-if="canEditUpload(upload)"
                          :class="[
                            'absolute inset-0 bg-black/50 transition-opacity grid grid-cols-2 gap-1 items-center justify-center content-center',
                            isPhotoOverlayActive(upload.id, index)
                              ? 'opacity-100 pointer-events-auto'
                              : 'opacity-0 pointer-events-none',
                            'sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto sm:group-focus-within:opacity-100 sm:group-focus-within:pointer-events-auto',
                          ]"
                        >
                          <!-- Bouton étoile pour passer en photo de couverture (uniquement pour les photos z) -->
                          <button
                            v-if="!isMainPhoto(image, upload.id)"
                            type="button"
                            class="p-2 bg-white/90 text-yellow-500 rounded-full hover:bg-white focus:outline-none disabled:opacity-60 transition-transform hover:scale-90"
                            :title="'Définir comme photo de couverture'"
                            :disabled="
                              isPhotoActionLoading(
                                upload.id,
                                getPhotoFilename(image),
                                'setCover',
                              )
                            "
                            @click.stop="handleSetAsCover(upload, image)"
                          >
                            <i class="fas fa-star text-sm"></i>
                          </button>
                          <button
                            type="button"
                            class="p-2 bg-white/90 text-blue-600 rounded-full hover:bg-white focus:outline-none disabled:opacity-60 transition-transform hover:scale-90"
                            :title="'Pivoter la photo'"
                            :disabled="
                              isPhotoActionLoading(
                                upload.id,
                                getPhotoFilename(image),
                                'rotate',
                              )
                            "
                            @click.stop="handleRotatePhoto(upload, image)"
                          >
                            <i class="fas fa-redo-alt text-sm"></i>
                          </button>
                          <a
                            :href="getOriginalPhotoUrl(upload.id, image)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="p-2 bg-white/90 text-black rounded-full hover:bg-white focus:outline-none transition-transform hover:scale-90 flex items-center justify-center"
                            title="Voir en taille originale"
                            @click.stop
                          >
                            <i class="fas fa-download text-sm"></i>
                          </a>
                          <button
                            v-if="
                              !isMainPhoto(image, upload.id) ||
                              upload.premel === '1'
                            "
                            type="button"
                            class="p-2 bg-white/90 text-red-600 rounded-full hover:bg-white focus:outline-none disabled:opacity-60 transition-transform hover:scale-90"
                            :title="'Supprimer la photo'"
                            :disabled="
                              isPhotoActionLoading(
                                upload.id,
                                getPhotoFilename(image),
                                'delete',
                              )
                            "
                            @click.stop="handleDeletePhoto(upload, image)"
                          >
                            <i class="fas fa-trash-alt text-sm"></i>
                          </button>
                          <!-- Placeholder pour maintenir la grille quand c'est la photo de couverture -->
                          <div v-else class="p-2"></div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="upload.images.length === 0"
                      class="w-20 h-20 bg-gray-100 border border-dashed rounded flex items-center justify-center"
                    >
                      <i class="fas fa-image text-black"></i>
                    </div>
                  </div>
                </td>

                <!-- 6. Action -->
                <td class="px-3 py-2 whitespace-nowrap align-top">
                  <button
                    @click="openDeleteModal(upload)"
                    class="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Mobile/iPad: cartes (< 900px) -->
      <div class="tablet:hidden grid grid-cols-1 gap-3">
        <div
          v-for="upload in paginatedUploads"
          :key="upload.id"
          class="bg-white border border-gray-200 rounded-md overflow-visible"
        >
          <!-- En-tête : référence + date -->
          <div
            class="px-3 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between"
          >
            <div class="flex items-center gap-2 min-w-0">
              <i class="fas fa-tag text-blue-400 text-xs flex-shrink-0"></i>
              <button
                type="button"
                class="text-[11px] font-bold text-blue-700 truncate hover:text-blue-900"
                @click="openProductSheetFromUpload(upload)"
              >
                {{ upload.reference }}
              </button>
              <span
                v-if="upload.isMain"
                class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-blue-100 text-blue-700 border border-blue-300 flex-shrink-0"
                >★ Principale</span
              >
              <span
                v-if="upload.premel === '1'"
                class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-200 flex-shrink-0"
                >Brouillon</span
              >
            </div>
            <span class="text-[11px] text-blue-500 flex-shrink-0 ml-2">{{
              formatDate(upload.dateCreation)
            }}</span>
          </div>

          <!-- Section infos produit -->
          <div
            class="px-3 py-3 flex items-center justify-between border-b border-gray-100"
          >
            <div class="min-w-0">
              <div class="text-sm font-semibold text-black truncate">
                {{ upload.categories.general }}
              </div>
              <div class="text-xs text-black mt-0.5 truncate">
                {{ upload.categories.type }} › {{ upload.categories.sousType }}
              </div>
              <div class="text-xs text-black mt-0.5">
                {{ getCollectionLabel(upload.collection) }}
              </div>
            </div>
            <!-- Bouton upload -->
            <div
              :class="[
                'flex-shrink-0 ml-3 w-16 h-16 border-2 border-dashed rounded-xl flex flex-col items-center justify-center transition-all duration-200',
                canEditUpload(upload)
                  ? isDragOver(upload.id)
                    ? 'border-blue-500 bg-blue-50 cursor-grabbing'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer bg-gray-50'
                  : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50',
              ]"
              @click="triggerFileDialog(upload)"
              @dragenter.prevent="handleDragEnter($event, upload.id)"
              @dragover.prevent="handleDragOver($event, upload.id)"
              @dragleave.prevent="handleDragLeave($event, upload.id)"
              @drop.prevent="handleDrop($event, upload)"
            >
              <span
                v-if="isUploadingPhotos(upload.id)"
                class="flex flex-col items-center gap-1"
              >
                <span
                  class="inline-block w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
                ></span>
                <span class="text-[9px] text-blue-600">En cours</span>
              </span>
              <template v-else>
                <i
                  class="fas fa-cloud-upload-alt text-lg"
                  :class="
                    canEditUpload(upload)
                      ? isDragOver(upload.id)
                        ? 'text-blue-500'
                        : 'text-blue-400'
                      : 'text-black'
                  "
                ></i>
                <span
                  class="text-[9px] mt-0.5 font-medium"
                  :class="canEditUpload(upload) ? 'text-black' : 'text-black'"
                >
                  {{ canEditUpload(upload) ? "Ajouter" : "N/A" }}
                </span>
                <span
                  v-if="(upload.nbPhotos ?? upload.images.length) > 0"
                  class="text-[9px] text-blue-500 font-bold"
                >
                  {{ upload.nbPhotos ?? upload.images.length }}p
                </span>
              </template>
            </div>
          </div>

          <!-- Section photos : défilement horizontal -->
          <div
            class="px-3 py-2 overflow-visible"
            @dragover.prevent="handlePhotoDragOver($event)"
            @drop.prevent="handlePhotoDrop($event, upload)"
          >
            <div v-if="upload.images.length > 0" class="relative mx-3">
              <!-- Flèche gauche -->
              <button
                v-if="upload.images.length > 2"
                type="button"
                class="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/95 border border-gray-200 rounded-full shadow-md flex items-center justify-center text-blue-400 hover:text-blue-600 hover:shadow-lg hover:scale-110 transition-all duration-200"
                @click.stop="scrollPhotos(upload.id, 'left')"
              >
                <i class="fas fa-angle-left text-base"></i>
              </button>
              <!-- Flèche droite -->
              <button
                v-if="upload.images.length > 2"
                type="button"
                class="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/95 border border-gray-200 rounded-full shadow-md flex items-center justify-center text-blue-400 hover:text-blue-600 hover:shadow-lg hover:scale-110 transition-all duration-200"
                @click.stop="scrollPhotos(upload.id, 'right')"
              >
                <i class="fas fa-angle-right text-base"></i>
              </button>
              <div
                :ref="(el) => setPhotoScrollRef(upload.id, el)"
                class="flex gap-2 overflow-x-auto pb-1"
                style="scrollbar-width: none"
              >
                <div
                  v-for="(image, index) in upload.images"
                  :key="`${upload.id}-${index}`"
                  :draggable="canEditUpload(upload)"
                  :class="[
                    'relative group flex-shrink-0 rounded-xl overflow-hidden flex items-center justify-center cursor-move transition-all bg-gray-100',
                    isMainPhoto(image, upload.id)
                      ? 'border-2 border-blue-500 shadow-md'
                      : 'border border-gray-200',
                    isDraggingPhoto(upload.id, index)
                      ? 'opacity-50 scale-95'
                      : '',
                    isDragOverPhoto(upload.id, index)
                      ? 'ring-2 ring-blue-400 ring-offset-1'
                      : '',
                  ]"
                  style="width: 104px; height: 104px"
                  @click="togglePhotoOverlay(upload.id, index)"
                  @dragstart="handlePhotoDragStart($event, upload, index)"
                  @dragend="handlePhotoDragEnd(upload.id, index)"
                  @dragover.prevent="
                    handlePhotoItemDragOver($event, upload.id, index)
                  "
                  @dragleave.prevent="handlePhotoItemDragLeave()"
                  @drop.prevent="handlePhotoDropOnItem($event, upload, index)"
                >
                  <img
                    :src="getPreviewPhotoUrl(image)"
                    :alt="`Photo ${index + 1}`"
                    loading="lazy"
                    class="max-w-full max-h-full object-contain rounded pointer-events-none"
                    @error="(event: Event) => { const target = event.target as HTMLImageElement; if (target) target.style.display = 'none'; }"
                  />
                  <!-- Badge couverture -->
                  <div
                    v-if="isMainPhoto(image, upload.id)"
                    class="absolute bottom-0 left-0 right-0 bg-blue-500/90 text-white text-[8px] font-semibold py-0.5 text-center leading-tight"
                  >
                    Couverture
                  </div>
                  <!-- Overlay actions -->
                  <div
                    v-if="canEditUpload(upload)"
                    :class="[
                      'absolute inset-0 bg-black/60 transition-opacity grid grid-cols-2 gap-1 items-center justify-items-center content-center p-2',
                      isPhotoOverlayActive(upload.id, index)
                        ? 'opacity-100 pointer-events-auto'
                        : 'opacity-0 pointer-events-none',
                      'tablet:opacity-0 tablet:pointer-events-none tablet:group-hover:opacity-100 tablet:group-hover:pointer-events-auto',
                    ]"
                  >
                    <button
                      v-if="!isMainPhoto(image, upload.id)"
                      type="button"
                      class="w-7 h-7 bg-white/90 text-yellow-500 rounded-full flex items-center justify-center focus:outline-none disabled:opacity-60"
                      title="Définir comme photo de couverture"
                      :disabled="
                        isPhotoActionLoading(
                          upload.id,
                          getPhotoFilename(image),
                          'setCover',
                        )
                      "
                      @click.stop="handleSetAsCover(upload, image)"
                    >
                      <i class="fas fa-star text-xs"></i>
                    </button>
                    <button
                      type="button"
                      class="w-7 h-7 bg-white/90 text-blue-600 rounded-full flex items-center justify-center focus:outline-none disabled:opacity-60"
                      title="Pivoter la photo"
                      :disabled="
                        isPhotoActionLoading(
                          upload.id,
                          getPhotoFilename(image),
                          'rotate',
                        )
                      "
                      @click.stop="handleRotatePhoto(upload, image)"
                    >
                      <i class="fas fa-redo-alt text-xs"></i>
                    </button>
                    <a
                      :href="getOriginalPhotoUrl(upload.id, image)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="w-7 h-7 bg-white/90 text-black rounded-full flex items-center justify-center focus:outline-none"
                      title="Voir en taille originale"
                      @click.stop
                      ><i class="fas fa-download text-xs"></i
                    ></a>
                    <button
                      v-if="
                        !isMainPhoto(image, upload.id) || upload.premel === '1'
                      "
                      type="button"
                      class="w-7 h-7 bg-white/90 text-red-600 rounded-full flex items-center justify-center focus:outline-none disabled:opacity-60"
                      title="Supprimer la photo"
                      :disabled="
                        isPhotoActionLoading(
                          upload.id,
                          getPhotoFilename(image),
                          'delete',
                        )
                      "
                      @click.stop="handleDeletePhoto(upload, image)"
                    >
                      <i class="fas fa-trash-alt text-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- end relative wrapper -->
            <div
              v-else
              class="flex flex-col items-center justify-center py-5 bg-gray-50 rounded-lg border border-dashed border-gray-200"
            >
              <i class="fas fa-camera text-2xl text-black mb-1.5"></i>
              <span class="text-xs font-medium text-black">Aucune photo</span>
              <span class="text-[10px] text-black mt-0.5"
                >Ajoutez des photos ci-dessus</span
              >
            </div>
          </div>

          <!-- Pied de carte : bouton supprimer -->
          <div
            class="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex justify-end"
          >
            <button
              @click="openDeleteModal(upload)"
              class="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg text-white bg-red-500 hover:bg-red-600 active:bg-red-700 transition-colors shadow-sm"
            >
              <i class="fas fa-trash-alt"></i>
              Supprimer
            </button>
          </div>
        </div>

        <div
          v-if="paginatedUploads.length === 0"
          class="text-center py-12 text-black"
        >
          <i class="fas fa-inbox text-4xl mb-3 block"></i>
          <p class="text-sm">Aucun upload trouvé</p>
        </div>
      </div>
    </div>
    <!-- Bottom Pagination -->
    <div class="pagination-inline mt-4">
      <UnifiedPagination
        :total-items="totalItems"
        :current-page="currentPage"
        :items-per-page="itemsPerPage"
        variant="product"
        item-label="produits"
        :items-per-page-options="[10, 25, 50, 100]"
        :show-first-last-buttons="true"
        @update:current-page="uploadPhotoStore.setPage($event)"
        @update:items-per-page="uploadPhotoStore.setItemsPerPage($event)"
      />
    </div>

    <div
      v-if="hasBrouillonProducts"
      class="mt-6 flex justify-center tablet:justify-end"
    >
      <button
        type="button"
        :disabled="isPreparingPublishDraftModal || isPublishingDraftProducts"
        class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg text-white bg-green-600 hover:bg-green-700 active:bg-green-800 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
        @click="handleGoToMel"
      >
        <span
          v-if="isPreparingPublishDraftModal || isPublishingDraftProducts"
          class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
        ></span>
        <i v-else class="fas fa-upload"></i>
        {{
          isPreparingPublishDraftModal
            ? "Préparation..."
            : isPublishingDraftProducts
            ? "Mise en ligne..."
            : "Mettre en ligne vos brouillons"
        }}
      </button>
    </div>
  </div>

  <ModalComp
    :isVisible="showPublishDraftConfirmModal"
    size="lg"
    @update:isVisible="
      (value: boolean) => {
        if (!value) closePublishDraftConfirmModal();
      }
    "
  >
    <template #header>
      <span>Confirmer la mise en ligne</span>
    </template>
    <template #body>
      <div class="space-y-4">
        <p class="text-sm text-black">{{ publishDraftConfirmMessage }}</p>
        <ul
          v-if="publishDraftReferences.length > 0"
          class="max-h-56 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100"
        >
          <li
            v-for="reference in publishDraftReferences"
            :key="reference"
            class="px-3 py-2 text-sm text-black"
          >
            {{ reference }}
          </li>
        </ul>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-black hover:bg-gray-50 transition-colors"
          :disabled="isPublishingDraftProducts"
          @click="() => closePublishDraftConfirmModal()"
        >
          Annuler
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors disabled:bg-green-300 disabled:cursor-not-allowed flex items-center gap-2"
          :disabled="isPublishingDraftProducts"
          @click="confirmPublishDraftProducts"
        >
          <span
            v-if="isPublishingDraftProducts"
            class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
          ></span>
          Mettre en ligne
        </button>
      </div>
    </template>
  </ModalComp>

  <!-- Modal: assistant en 2 étapes pour la mise en ligne des brouillons -->
  <ModalComp
    :isVisible="chooseMainModalVisible"
    size="lg"
    @update:isVisible="(value: boolean) => { if (!value) closeUnifiedPublishModal() }"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <span v-if="publishStep === 1">
          Étape 1 — Choisir la couleur principale
        </span>
        <span v-else>
          <template v-if="hasChooseMainStep">Étape 2 — </template>Confirmer la
          mise en ligne
        </span>
        <span v-if="hasChooseMainStep" class="ml-2 flex items-center gap-1">
          <span
            :class="[
              'w-2 h-2 rounded-full',
              publishStep === 1 ? 'bg-orange-500' : 'bg-gray-300',
            ]"
          ></span>
          <span
            :class="[
              'w-2 h-2 rounded-full',
              publishStep === 2 ? 'bg-orange-500' : 'bg-gray-300',
            ]"
          ></span>
        </span>
      </div>
    </template>
    <template #body>
      <div class="max-h-[60vh] overflow-y-auto pr-1">
        <!-- Étape 1 : choisir la nouvelle principale -->
        <div v-if="publishStep === 1" class="space-y-5">
          <p class="text-sm text-gray-600">
            Certaines couleurs principales n'ont pas de photo et ne peuvent pas
            être mises en ligne. Choisissez pour chaque groupe une autre couleur
            (avec photo) qui deviendra la nouvelle principale. L'ancienne
            principale restera en brouillon comme variante.
          </p>

          <div
            v-for="g in chooseMainGroups"
            :key="g.oldMainId"
            class="border border-gray-200 rounded-lg p-3"
          >
            <p class="text-xs font-medium text-gray-700 mb-2">
              Groupe « {{ g.oldMainRef }} » — choisir la nouvelle principale :
            </p>
            <div class="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              <button
                v-for="c in g.candidates"
                :key="c.id"
                type="button"
                @click="g.selectedId = c.id"
                :class="[
                  'flex-shrink-0 w-28 rounded-lg border-2 p-2 transition-all text-left',
                  g.selectedId === c.id
                    ? 'border-orange-500 bg-orange-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300',
                ]"
              >
                <div
                  class="relative w-full aspect-square bg-gray-100 rounded-md overflow-hidden mb-2"
                >
                  <img
                    v-if="c.images && c.images.length"
                    :src="c.images[0]"
                    :alt="c.reference"
                    loading="lazy"
                    class="w-full h-full object-cover"
                  />
                  <div v-else class="flex items-center justify-center h-full">
                    <i class="fas fa-tshirt text-gray-300 text-base"></i>
                  </div>
                  <div
                    v-if="g.selectedId === c.id"
                    class="absolute top-1 right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center shadow"
                  >
                    <i class="fas fa-check text-white text-[10px]"></i>
                  </div>
                </div>
                <div
                  class="text-[11px] font-medium text-gray-900 truncate"
                  :title="c.reference"
                >
                  {{ c.reference }}
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Étape 2 : liste des produits normaux à mettre en ligne -->
        <div v-else class="space-y-3">
          <p class="text-sm text-gray-600">
            Les produits brouillons ci-dessous (avec au moins une photo) vont
            être mis en ligne.
          </p>
          <ul
            class="divide-y divide-gray-100 border border-gray-200 rounded-lg"
          >
            <li
              v-for="ref in publishDraftReferences"
              :key="ref"
              class="px-3 py-2 text-sm text-gray-800"
            >
              {{ ref }}
            </li>
            <li
              v-if="publishDraftReferences.length === 0"
              class="px-3 py-2 text-sm text-gray-400 italic"
            >
              Aucun produit supplémentaire à mettre en ligne.
            </li>
          </ul>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          :disabled="isChoosingMain"
          @click="closeUnifiedPublishModal()"
        >
          Annuler
        </button>
        <button
          v-if="publishStep === 2 && hasChooseMainStep"
          type="button"
          class="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          :disabled="isChoosingMain"
          @click="goToPublishStep(1)"
        >
          Précédent
        </button>
        <button
          v-if="publishStep === 1"
          type="button"
          class="px-3 py-1.5 text-sm rounded-md text-white bg-orange-500 hover:bg-orange-600 transition-colors"
          @click="goToPublishStep(2)"
        >
          Suivant
        </button>
        <button
          v-else
          type="button"
          class="px-3 py-1.5 text-sm rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors disabled:bg-green-300 disabled:cursor-not-allowed flex items-center gap-2"
          :disabled="isChoosingMain"
          @click="confirmChooseMainAndPublish"
        >
          <span
            v-if="isChoosingMain"
            class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
          ></span>
          Mettre en ligne
        </button>
      </div>
    </template>
  </ModalComp>

  <ModalComp
    :isVisible="showDeleteModal"
    size="sm"
    @update:isVisible="(value: boolean) => { if (!value) closeDeleteModal() }"
  >
    <template #header>
      <span>Confirmer la suppression</span>
    </template>
    <template #body>
      <p class="text-sm text-black">
        Êtes-vous sûr de vouloir supprimer la référence
        <span class="font-semibold text-black">
          {{ uploadPendingDeletion?.reference }}
        </span>
        ?
      </p>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-black hover:bg-gray-50 transition-colors"
          :disabled="isDeletingUpload"
          @click="closeDeleteModal"
        >
          Annuler
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors disabled:bg-red-300 disabled:cursor-not-allowed flex items-center gap-2"
          :disabled="isDeletingUpload"
          @click="confirmDeleteUpload"
        >
          <span
            v-if="isDeletingUpload"
            class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
          ></span>
          Supprimer
        </button>
      </div>
    </template>
  </ModalComp>

  <!-- Modal de confirmation pour la suppression de photo -->
  <ModalComp
    :is-visible="showDeletePhotoModal"
    size="sm"
    @update:isVisible="(value: boolean) => { if (!value) { showDeletePhotoModal = false; photoPendingDeletion = null; } }"
  >
    <template #header>
      <span>Confirmer la suppression</span>
    </template>
    <template #body>
      <div class="space-y-3">
        <p class="text-sm text-black">
          Êtes-vous sûr de vouloir supprimer cette photo ?
        </p>
        <div
          v-if="photoPendingDeletion"
          class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
        >
          <div
            class="w-16 h-16 bg-gray-200 rounded overflow-hidden flex-shrink-0"
          >
            <img
              :src="getPreviewPhotoUrl(photoPendingDeletion.photoUrl)"
              alt="Photo à supprimer"
              class="w-full h-full object-cover"
            />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-black">
              {{ photoPendingDeletion.upload.reference }}
            </p>
            <p class="text-xs text-black truncate">
              {{ getPhotoFilename(photoPendingDeletion.photoUrl) }}
            </p>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-black hover:bg-gray-50 transition-colors"
          @click="
            showDeletePhotoModal = false;
            photoPendingDeletion = null;
          "
        >
          Annuler
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors disabled:bg-red-300 disabled:cursor-not-allowed flex items-center gap-2"
          @click="confirmDeletePhoto"
        >
          <i class="fas fa-trash-alt text-xs"></i>
          Supprimer la photo
        </button>
      </div>
    </template>
  </ModalComp>

  <ProductSheetModal
    :visible="ficheModalVisible"
    :product="activeProductForSheet"
    :brands="brands"
    :categories-tree="categoriesTree"
    :provenances="provenances"
    :packs="packs"
    :declinaisons="declinaisons"
    @update:visible="handleSheetVisibleUpdate"
    @save="handleSaveProductSheet"
    @pending-cover="handlePendingCover"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import {
  PageHeader,
  UploadProductFilters,
  UnifiedPagination,
  ModalComp,
  ConfirmModal,
} from "@/components";
import type {
  CategoryOption,
  PhotoUpload,
  ProduitCatalogue,
  UploadProductApiItem,
  UploadProductFiltersData,
} from "@/types";
import {
  getUploadProducts,
  getReferenceData,
  deleteProduct,
  getProductPhotoTooLargeErrorMessage,
  getUnsupportedProductPhotoErrorMessage,
  isSupportedProductPhotoSize,
  uploadProductPhotos,
  uploadProductPhotosBulkZip,
  getProductPhotos,
  getProductPhotosBatch,
  isSupportedProductPhotoFormat,
  productPhotoInputAccept,
  deleteProductPhoto,
  rotateProductPhoto,
  reorderProductPhotos,
} from "@/api/shooting";
import { publishBrouillonBulk, toggleMainProduct } from "@/api/produits";
import { useAuthStore } from "@/stores/auth";
import { useNotification } from "@/composables/useNotification";
import { useUploadPhotoStore } from "@/stores/uploadPhoto";
import { getCategoriesTree } from "@/api/categories";
import type { CategorieNode } from "@/types/categorie";
import ProductSheetModal from "@/components/modals/ProductSheetModal.vue";
import type { PackOption } from "@/types/packOption";
import {
  getProductsPage,
  getProvenances,
  type Produit,
  type VendeurDeclinaison,
} from "@/api/produits";
import {
  getApiBaseUrl,
  getProductImageUrl,
  getProductOriginalImageUrl,
} from "@/config/api";
import { bumpPhotoVersion, getPhotoVersion } from "@/utils/photoVersion";

const apiBaseUrl = getApiBaseUrl();

// Router
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { success, error: showError } = useNotification();

// Récupérer le ou les shootingId depuis l'URL si présent (un ou plusieurs)
const shootingIdFromUrl = computed(
  () => route.query.shootingId as string | string[] | undefined,
);

// Tableau d'IDs numériques pour l'API (un ou plusieurs shootings)
const shootingIdsFromUrl = computed(() => {
  const raw = shootingIdFromUrl.value;
  if (raw == null) return undefined;
  const arr = Array.isArray(raw) ? raw : [raw];
  const ids = arr
    .map((s) => parseInt(String(s), 10))
    .filter((n) => !Number.isNaN(n));
  return ids.length > 0 ? ids : undefined;
});

// Pinia store pour conserver l'état de la page pendant la session SPA.
const uploadPhotoStore = useUploadPhotoStore();
const { currentPage, itemsPerPage, currentFilters } =
  storeToRefs(uploadPhotoStore);

const getShootingContextKey = () =>
  shootingIdsFromUrl.value?.join(",") || undefined;

const syncShootingContext = () => {
  const nextShootingContext = getShootingContextKey();
  const contextChanged =
    uploadPhotoStore.shootingIdFromUrl !== nextShootingContext;

  uploadPhotoStore.setShootingId(nextShootingContext);

  if (contextChanged && currentPage.value !== 1) {
    uploadPhotoStore.setPage(1);
    return true;
  }

  return false;
};

// État global de la page
const uploads = ref<PhotoUpload[]>([]);
const filteredUploads = ref<PhotoUpload[]>([]);
const totalItems = ref(0); // Total réel des éléments depuis l'API
const brouillonTotalCount = ref(0);
const hasBrouillonProducts = computed(() => brouillonTotalCount.value > 0);
const referenceDataLoaded = ref(false);
const isLoadingReferenceData = ref(false);
const isLoading = ref(false);
const categoryLabels = ref({
  general: {} as Record<string, string>,
  type: {} as Record<string, string>,
  sousType: {} as Record<string, string>,
});
const collectionLabels = ref<Record<string, string>>({});
const brands = ref<Array<{ id_vendeur_marque: number; nom: string }>>([]);
const categoryOptions = ref<CategoryOption[]>([]);
const sousCategoryOptions = ref<CategoryOption[]>([]);
const sousSousCategoryOptions = ref<CategoryOption[]>([]);
const categoriesTree = ref<CategorieNode[]>([]);
const showDeleteModal = ref(false);
const uploadPendingDeletion = ref<PhotoUpload | null>(null);
const isDeletingUpload = ref(false);
const showDeletePhotoModal = ref(false);
const photoPendingDeletion = ref<{
  upload: PhotoUpload;
  photoUrl: string;
} | null>(null);
const ficheModalVisible = ref(false);
const activeProductForSheet = ref<ProduitCatalogue | null>(null);
const pendingSheetSuccessMessage = ref<string | null>(null);
const provenances = ref<{ id_provenance: number; label: string }[]>([]);
const packs = ref<PackOption[]>([]);
const declinaisons = ref<VendeurDeclinaison[]>([]);
const uploadingProductIds = ref<number[]>([]);
const photoActionsInProgress = ref<Set<string>>(new Set());
const photoCacheBuster = ref<Record<number, number>>({});
const dragOverProductIds = ref<Set<number>>(new Set());
const draggingPhoto = ref<{ uploadId: number; index: number } | null>(null);
const dragOverPhotoIndex = ref<{ uploadId: number; index: number } | null>(
  null,
);
const activePhotoOverlay = ref<{ uploadId: number; index: number } | null>(
  null,
);
const isReorderingPhotos = ref<Set<number>>(new Set());
// Upload ZIP groupé
const zipFileInputRef = ref<HTMLInputElement | null>(null);
const selectedZipFile = ref<File | null>(null);
const isZipUploading = ref(false);
const zipDragOver = ref(false);
const zipErrorMessage = ref("");
const zipErrors = ref<string[]>([]);
const showZipErrors = ref(false);
const activeUploadMode = ref<"zip" | null>(null);
const toggleUploadMode = (mode: "zip") => {
  activeUploadMode.value = activeUploadMode.value === mode ? null : mode;
};
const showPublishDraftConfirmModal = ref(false);
const isPreparingPublishDraftModal = ref(false);
const isPublishingDraftProducts = ref(false);
const publishDraftCandidates = ref<Array<{ id: number; reference: string }>>(
  [],
);
// Modal "choisir une nouvelle principale" pour les groupes dont la principale n'a pas de photo
const chooseMainModalVisible = ref(false);
const chooseMainGroups = ref<
  {
    oldMainId: number;
    oldMainRef: string;
    selectedId: number;
    candidates: PhotoUpload[];
  }[]
>([]);
const isChoosingMain = ref(false);
// Assistant en 2 étapes pour la mise en ligne des brouillons :
//   étape 1 = choisir une nouvelle principale (groupes dont la principale n'a pas de photo)
//   étape 2 = liste des produits normaux à mettre en ligne
const publishStep = ref<1 | 2>(1);
const hasChooseMainStep = computed(() => chooseMainGroups.value.length > 0);
const goToPublishStep = (step: 1 | 2) => {
  publishStep.value = step;
};
// Évite les races: seule la dernière requête upload peut mettre à jour l'UI
const loadUploadsRequestSeq = ref(0);
// Les filtres actifs sont maintenant persistés via Pinia -> currentFilters

// Pagination
// Avec pagination côté serveur, on affiche directement les données chargées
const paginatedUploads = computed<PhotoUpload[]>(() => {
  return filteredUploads.value;
});

// Méthodes utilitaires
const formatDate = (dateValue: string | Date | null | undefined): string => {
  if (!dateValue) return "-";

  try {
    if (typeof dateValue === "string") {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return dateValue;
      }
      return date.toLocaleDateString("fr-FR");
    }
    return String(dateValue);
  } catch (error) {
    return "-";
  }
};

const getCollectionLabel = (collection: string): string => {
  if (!collection) {
    return "";
  }

  return collectionLabels.value[collection] || collection;
};

const ensureReferenceDataLoaded = async () => {
  if (referenceDataLoaded.value || isLoadingReferenceData.value) {
    return;
  }

  try {
    isLoadingReferenceData.value = true;
    const data = await getReferenceData();

    const categoriesList: CategoryOption[] = (data.categories || []).map(
      (cat: { id: string | number; label: string }) => ({
        value: String(cat.id),
        label: cat.label,
      }),
    );

    const sousCategoriesList: CategoryOption[] = (
      data.sousCategories || []
    ).map((cat: { id: string | number; label: string }) => {
      const idString = String(cat.id);
      return {
        value: idString,
        label: cat.label,
        parentId: idString.slice(0, 2),
      };
    });

    const sousSousCategoriesList: CategoryOption[] = (
      data.sousSousCategories || []
    ).map((cat: { id: string | number; label: string }) => {
      const idString = String(cat.id);
      return {
        value: idString,
        label: cat.label,
        parentId: idString.slice(0, 4),
      };
    });

    categoryOptions.value = categoriesList;
    sousCategoryOptions.value = sousCategoriesList;
    sousSousCategoryOptions.value = sousSousCategoriesList;

    categoryLabels.value.general = Object.fromEntries(
      categoriesList.map((cat) => [cat.value, cat.label]),
    );
    categoryLabels.value.type = Object.fromEntries(
      sousCategoriesList.map((cat) => [cat.value, cat.label]),
    );
    categoryLabels.value.sousType = Object.fromEntries(
      sousSousCategoriesList.map((cat) => [cat.value, cat.label]),
    );

    collectionLabels.value = Object.fromEntries(
      (data.collections || []).map(
        (collection: { id: string | number; label: string }) => [
          String(collection.id),
          collection.label,
        ],
      ),
    );

    brands.value = (data.marques || []).map(
      (brand: {
        id?: string | number;
        id_vendeur_marque?: string | number;
        label?: string;
        nom?: string;
      }) => ({
        id_vendeur_marque: Number(brand.id_vendeur_marque ?? brand.id ?? 0),
        nom: String(brand.nom ?? brand.label ?? ""),
      }),
    );

    packs.value = (data.packs || []).map(
      (pack: { id: string | number; label: string; value: string }) => ({
        id_pack: Number(pack.id),
        id_vendeur: Number(
          authStore.user?.id ?? authStore.user?.id_vendeur ?? 0,
        ),
        titre: pack.label,
        valeur: pack.value,
      }),
    );

    declinaisons.value = (data.declinaisons || []).map(
      (declinaison: {
        id: string | number;
        label: string;
        tailles?: unknown[];
      }) =>
        ({
          id_declinaison: Number(declinaison.id),
          id_vendeur: Number(
            authStore.user?.id ?? authStore.user?.id_vendeur ?? 0,
          ),
          titre: declinaison.label,
          tailles: declinaison.tailles,
        } as VendeurDeclinaison),
    );

    provenances.value = await getProvenances("fr");

    referenceDataLoaded.value = true;
  } catch (error) {
    // Erreur silencieuse
  } finally {
    isLoadingReferenceData.value = false;
  }
};

const resolveCategoryInfo = (categoryId: number | null | undefined) => {
  if (!categoryId) {
    return {
      general: "",
      type: "",
      sousType: "",
      generalId: "",
      typeId: "",
      sousTypeId: "",
    };
  }

  const idString = String(categoryId);
  const generalId = idString.slice(0, 2);
  const typeId = idString.length >= 4 ? idString.slice(0, 4) : "";
  const sousTypeId = idString.length >= 6 ? idString.slice(0, 6) : "";

  return {
    general: categoryLabels.value.general[generalId] || "",
    type: typeId ? categoryLabels.value.type[typeId] || "" : "",
    sousType: sousTypeId ? categoryLabels.value.sousType[sousTypeId] || "" : "",
    generalId,
    typeId,
    sousTypeId,
  };
};

const resolveCollectionId = (collectionId: number | null | undefined) => {
  if (!collectionId && collectionId !== 0) {
    return "";
  }
  return String(collectionId);
};

const mapUploadProductApiItemToPhotoUpload = (
  item: UploadProductApiItem,
): PhotoUpload => {
  const categoriesInfo = resolveCategoryInfo(item.idCategorie ?? null);
  return {
    id: item.id,
    reference: item.reference || "",
    referenceBase: item.referenceBase ?? null,
    idShooting: item.idShooting ?? item.shootingId ?? undefined,
    dateCreation: item.dateCreation ?? null,
    premel: item.premel ?? null,
    categories: categoriesInfo,
    collection: resolveCollectionId(item.idCollection ?? null),
    nbPhotos: item.nbPhotos ?? 0,
    images: [] as string[],
    statut: "pending" as const,
    isMain: item.main === 1,
  };
};

const sortUploads = (
  list: PhotoUpload[],
  tri: UploadProductFiltersData["tri"] | undefined,
) => {
  const sortKey = tri || "reference-asc";
  const sorted: PhotoUpload[] = [...list];

  const compareReference = (a: PhotoUpload, b: PhotoUpload) => {
    const refA = (a.reference || "").toString().toUpperCase();
    const refB = (b.reference || "").toString().toUpperCase();
    return refA.localeCompare(refB, "fr", {
      sensitivity: "base",
      numeric: true,
    });
  };

  const parseDate = (value: string | Date | null | undefined) => {
    if (!value) return 0;
    const date = typeof value === "string" ? new Date(value) : new Date(value);
    const timestamp = date.getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  // Grouper par referenceBase et obtenir la date max de chaque groupe
  const getGroupMaxDate = (items: PhotoUpload[]) => {
    const groupDates: Record<string, number> = {};
    for (const item of items) {
      const key = item.referenceBase || item.reference || "";
      const date = parseDate(item.dateCreation);
      if (!groupDates[key] || date > groupDates[key]) {
        groupDates[key] = date;
      }
    }
    return groupDates;
  };

  const getGroupMinDate = (items: PhotoUpload[]) => {
    const groupDates: Record<string, number> = {};
    for (const item of items) {
      const key = item.referenceBase || item.reference || "";
      const date = parseDate(item.dateCreation);
      if (!groupDates[key] || date < groupDates[key]) {
        groupDates[key] = date;
      }
    }
    return groupDates;
  };

  switch (sortKey) {
    case "reference-desc":
      sorted.sort((a, b) => compareReference(b, a));
      break;
    case "date-asc": {
      // Trier par date croissante, grouper par referenceBase
      const groupMinDates = getGroupMinDate(sorted);
      sorted.sort((a, b) => {
        const keyA = a.referenceBase || a.reference || "";
        const keyB = b.referenceBase || b.reference || "";
        // D'abord comparer par date min du groupe
        const groupDateDiff = groupMinDates[keyA] - groupMinDates[keyB];
        if (groupDateDiff !== 0) return groupDateDiff;
        // Si même groupe (même referenceBase), trier par date individuelle
        if (keyA === keyB) {
          return parseDate(a.dateCreation) - parseDate(b.dateCreation);
        }
        // Sinon trier par referenceBase
        return keyA.localeCompare(keyB, "fr", {
          sensitivity: "base",
          numeric: true,
        });
      });
      break;
    }
    case "date-desc": {
      // Trier par date décroissante, grouper par referenceBase
      const groupMaxDates = getGroupMaxDate(sorted);
      sorted.sort((a, b) => {
        const keyA = a.referenceBase || a.reference || "";
        const keyB = b.referenceBase || b.reference || "";
        // D'abord comparer par date max du groupe (décroissant)
        const groupDateDiff = groupMaxDates[keyB] - groupMaxDates[keyA];
        if (groupDateDiff !== 0) return groupDateDiff;
        // Si même groupe (même referenceBase), trier par date individuelle (décroissant)
        if (keyA === keyB) {
          return parseDate(b.dateCreation) - parseDate(a.dateCreation);
        }
        // Sinon trier par referenceBase
        return keyA.localeCompare(keyB, "fr", {
          sensitivity: "base",
          numeric: true,
        });
      });
      break;
    }
    case "reference-asc":
    default:
      sorted.sort(compareReference);
      break;
  }

  return sorted;
};

const buildPhotoUrl = (photo: string, uploadId: number): string => {
  const absolute = getProductImageUrl(photo);
  // Buster en mémoire (opération en cours) sinon version persistante (localStorage)
  const buster = photoCacheBuster.value[uploadId] ?? getPhotoVersion(uploadId);
  if (!buster) {
    return absolute;
  }

  const separator = absolute.includes("?") ? "&" : "?";
  return `${absolute}${separator}t=${buster}`;
};

// Aperçu: préférer les variantes redimensionnées côté serveur (héritées IMGCOMPRESS)
// - remplace /original/ ou /zoom/ par /liste/ (pour meilleure performance d'affichage)
// - tout est déjà en .jpg (comme dans l'ancien système)
// - conserve la query string (cache buster)
// - si déjà en /fiche/ ou /liste/, on garde tel quel
// - fallback: si /liste/ n'existe pas, utiliser /zoom/ ou /original/
const getPreviewPhotoUrl = (photoUrl: string): string => {
  if (!photoUrl) return "";
  try {
    const [base, query = ""] = photoUrl.split("?");
    let previewBase = base;

    // Gérer le nouveau chemin avec /Produits/original/ (priorité)
    if (base.includes("/Produits/original/")) {
      // Essayer d'abord /Produits/liste/ (version optimisée pour l'affichage)
      previewBase = base.replace("/Produits/original/", "/Produits/liste/");
      // Si /liste/ n'existe pas, fallback vers /zoom/ (plus petit que original)
      // Le navigateur gérera le 404 et chargera l'image de fallback si nécessaire
    }
    // Gérer le nouveau chemin avec /Produits/zoom/
    else if (base.includes("/Produits/zoom/")) {
      previewBase = base.replace("/Produits/zoom/", "/Produits/liste/");
    }
    // Gérer l'ancien chemin /original/
    else if (base.includes("/original/")) {
      previewBase = base.replace("/original/", "/liste/");
    }
    // Gérer l'ancien chemin /zoom/
    else if (base.includes("/zoom/")) {
      previewBase = base.replace("/zoom/", "/liste/");
    }
    // Si déjà en /fiche/ ou /liste/, on garde tel quel
    else if (
      base.includes("/fiche/") ||
      base.includes("/liste/") ||
      base.includes("/Produits/fiche/") ||
      base.includes("/Produits/liste/")
    ) {
      previewBase = base;
    }

    return query ? `${previewBase}?${query}` : previewBase;
  } catch {
    return photoUrl;
  }
};

/** Récupère les photos d’un seul upload (ex. après réorganisation / suppression). */
const fetchProductPhotos = async (upload: PhotoUpload) => {
  try {
    const response = await getProductPhotos(upload.id);
    if (response?.success) {
      upload.images = response.photos
        ? response.photos.map((photo) => buildPhotoUrl(photo, upload.id))
        : [];
      upload.nbPhotos = response.nbPhotos ?? upload.images.length;
    } else {
      upload.images = [];
      upload.nbPhotos = upload.images.length;
    }
  } catch (error) {
    upload.images = [];
    upload.nbPhotos = upload.images.length;
  }
};

/** Récupère toutes les photos (c + z) de tous les uploads en un seul appel API. */
const fetchProductPhotosBatch = async (uploadList: PhotoUpload[]) => {
  if (!uploadList.length) return;
  const ids = uploadList.map((u) => u.id);
  const { success: ok, photosByProduct } = await getProductPhotosBatch(
    ids,
    true,
  );
  if (!ok || !photosByProduct) return;
  for (const upload of uploadList) {
    const photos = photosByProduct[upload.id];
    upload.images = Array.isArray(photos)
      ? photos.map((photo) => buildPhotoUrl(photo, upload.id))
      : [];
    upload.nbPhotos = upload.images.length;
  }
};

const applyFiltersAndSort = () => {
  const tri = currentFilters.value.tri || "reference-asc";
  const selectedCategorie = currentFilters.value.categorie || "";
  const selectedSousCategorie = currentFilters.value.sousCategorie || "";
  const selectedSousSousCategorie =
    currentFilters.value.sousSousCategorie || "";
  const hasPhotos = currentFilters.value.hasPhotos || "";
  const premelFilter = currentFilters.value.premelFilter || "";

  let workingList = [...uploads.value];

  if (selectedCategorie) {
    workingList = workingList.filter(
      (upload) => upload.categories.generalId === selectedCategorie,
    );
  }

  if (selectedSousCategorie) {
    workingList = workingList.filter(
      (upload) => upload.categories.typeId === selectedSousCategorie,
    );
  }

  if (selectedSousSousCategorie) {
    workingList = workingList.filter(
      (upload) => upload.categories.sousTypeId === selectedSousSousCategorie,
    );
  }

  // Filtrer selon présence de photos
  if (hasPhotos === "with") {
    workingList = workingList.filter(
      (upload) => (upload.nbPhotos ?? upload.images?.length ?? 0) > 0,
    );
  } else if (hasPhotos === "without") {
    workingList = workingList.filter(
      (upload) => (upload.nbPhotos ?? upload.images?.length ?? 0) === 0,
    );
  }

  // Filtrer selon statut de mise en ligne
  if (premelFilter === "en_ligne") {
    workingList = workingList.filter(
      (upload) => (upload.premel ?? "0") !== "1",
    );
  } else if (premelFilter === "brouillon") {
    workingList = workingList.filter((upload) => upload.premel === "1");
  }

  filteredUploads.value = sortUploads(workingList, tri);

  // La pagination est faite côté serveur. Ne pas recalculer la page max avec
  // la seule page chargée, sinon le numéro de page peut être écrasé à tort.
};

const setUploadingProduct = (productId: number, isUploading: boolean) => {
  if (isUploading) {
    if (!uploadingProductIds.value.includes(productId)) {
      uploadingProductIds.value = [...uploadingProductIds.value, productId];
    }
  } else {
    uploadingProductIds.value = uploadingProductIds.value.filter(
      (id) => id !== productId,
    );
  }
};

const setPhotoCacheBuster = (uploadId: number) => {
  photoCacheBuster.value = {
    ...photoCacheBuster.value,
    [uploadId]: bumpPhotoVersion(uploadId),
  };
};

const setPhotoActionState = (key: string, inProgress: boolean) => {
  const updated = new Set(photoActionsInProgress.value);
  if (inProgress) {
    updated.add(key);
  } else {
    updated.delete(key);
  }
  photoActionsInProgress.value = updated;
};

const isPhotoActionLoading = (
  uploadId: number,
  filename: string,
  action: "delete" | "rotate" | "setCover",
) => photoActionsInProgress.value.has(`${uploadId}:${filename}:${action}`);

const isUploadingPhotos = (productId: number) =>
  uploadingProductIds.value.includes(productId);

const handlePhotosUpload = async (
  upload: PhotoUpload,
  filesInput: FileList | File[],
) => {
  if (!canEditUpload(upload)) {
    return;
  }

  const filesArray = Array.isArray(filesInput)
    ? filesInput
    : Array.from(filesInput ?? []);

  if (!filesArray.length) {
    return;
  }

  const oversizedFiles = filesArray.filter(
    (file) => !isSupportedProductPhotoSize(file),
  );
  const unsupportedFiles = filesArray.filter(
    (file) => !isSupportedProductPhotoFormat(file),
  );
  const validFiles = filesArray.filter(
    (file) =>
      isSupportedProductPhotoFormat(file) && isSupportedProductPhotoSize(file),
  );

  if (validFiles.length === 0) {
    if (oversizedFiles.length > 0 && unsupportedFiles.length === 0) {
      showError(getProductPhotoTooLargeErrorMessage());
      return;
    }

    if (unsupportedFiles.length > 0 && oversizedFiles.length === 0) {
      showError(getUnsupportedProductPhotoErrorMessage());
      return;
    }

    showError(
      `${getUnsupportedProductPhotoErrorMessage()} ${getProductPhotoTooLargeErrorMessage()}`,
    );
    return;
  }

  if (validFiles.length !== filesArray.length) {
    const reasons: string[] = [];
    if (unsupportedFiles.length > 0) {
      reasons.push("format non pris en charge");
    }
    if (oversizedFiles.length > 0) {
      reasons.push("taille trop importante");
    }
    showError(`Certaines photos ont été ignorées (${reasons.join(" et ")}).`);
  }

  setUploadingProduct(upload.id, true);

  try {
    const response = await uploadProductPhotos(upload.id, validFiles);
    if (response?.success) {
      setPhotoCacheBuster(upload.id);
      upload.images = response.photos
        ? response.photos.map((photo) => buildPhotoUrl(photo, upload.id))
        : [];
      upload.nbPhotos = response.nbPhotos ?? upload.images.length;
      success(response.message || "Photos uploadées avec succès");
    } else {
      showError(response?.message || "Erreur lors de l’upload des photos");
    }
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "Erreur lors de l’upload des photos";
    showError(message);
  } finally {
    setUploadingProduct(upload.id, false);
  }
};

const triggerFileDialog = (upload: PhotoUpload) => {
  if (!canEditUpload(upload)) {
    return;
  }

  const input = document.createElement("input");
  input.type = "file";
  input.accept = productPhotoInputAccept;
  input.multiple = true;
  input.addEventListener("change", (event) => {
    const target = event.target as HTMLInputElement;
    if (target?.files && target.files.length > 0) {
      handlePhotosUpload(upload, target.files);
    }
    target.value = "";
    input.remove();
  });
  input.click();
};

const handleDragEnter = (event: DragEvent, productId: number) => {
  event.preventDefault();
  event.stopPropagation();
  // Vérifier si les fichiers sont des images
  if (event.dataTransfer?.types.includes("Files")) {
    dragOverProductIds.value.add(productId);
  }
};

const handleDragOver = (event: DragEvent, productId: number) => {
  event.preventDefault();
  event.stopPropagation();
  // Maintenir l'état de drag over
  if (event.dataTransfer?.types.includes("Files")) {
    dragOverProductIds.value.add(productId);
  }
};

const handleDragLeave = (event: DragEvent, productId: number) => {
  event.preventDefault();
  event.stopPropagation();
  // Vérifier si on quitte vraiment la zone (pas juste un enfant)
  const relatedTarget = event.relatedTarget as HTMLElement | null;
  const currentTarget = event.currentTarget as HTMLElement | null;
  if (!currentTarget?.contains(relatedTarget)) {
    dragOverProductIds.value.delete(productId);
  }
};

const handleDrop = (event: DragEvent, upload: PhotoUpload) => {
  event.preventDefault();
  event.stopPropagation();
  dragOverProductIds.value.delete(upload.id);

  if (!canEditUpload(upload)) {
    return;
  }

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    handlePhotosUpload(upload, files);
  }
};

const isDragOver = (productId: number): boolean => {
  return dragOverProductIds.value.has(productId);
};

const getPhotoFilename = (photoUrl: string): string => {
  if (!photoUrl) {
    return "";
  }

  try {
    const url = new URL(photoUrl, apiBaseUrl);
    const segments = url.pathname.split("/");
    return segments.pop() ?? "";
  } catch {
    const sanitized = photoUrl.split("?")[0] ?? "";
    const segments = sanitized.split("/");
    return segments.pop() ?? "";
  }
};

/** URL pour ouvrir la photo en taille originale dans un nouvel onglet (comme précommande). */
const getOriginalPhotoUrl = (uploadId: number, photoUrl: string): string => {
  void uploadId;
  return getProductOriginalImageUrl(photoUrl);
};

// Fonction pour détecter si une photo est la photo principale (c)
const isMainPhoto = (photoUrl: string, productId: number): boolean => {
  const filename = getPhotoFilename(photoUrl);
  if (!filename) {
    return false;
  }

  // Vérifier si le nom de fichier correspond au format {productId}-c.{ext}
  const pattern = new RegExp(`^${productId}-c\\.`);
  return pattern.test(filename);
};

// Fonction pour extraire la position d'une photo (c, z-1, z-2, etc.)
const getPhotoPosition = (photoUrl: string, productId: number): string => {
  const filename = getPhotoFilename(photoUrl);
  if (!filename) {
    return "";
  }

  // Extraire la position du format {productId}-{position}.{ext}
  const match = filename.match(new RegExp(`^${productId}-(c|z-\\d+)\\.`));
  return match ? match[1] : "";
};

// Gestion du drag and drop pour réorganiser les photos
const handlePhotoDragStart = (
  event: DragEvent,
  upload: PhotoUpload,
  index: number,
) => {
  if (!canEditUpload(upload)) {
    event.preventDefault();
    return;
  }

  draggingPhoto.value = { uploadId: upload.id, index };
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", "");
  }
};

const handlePhotoDragEnd = (uploadId: number, index: number) => {
  if (
    draggingPhoto.value?.uploadId === uploadId &&
    draggingPhoto.value?.index === index
  ) {
    draggingPhoto.value = null;
  }
  dragOverPhotoIndex.value = null;
};

const handlePhotoDragOver = (event: DragEvent) => {
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "move";
  }
};

const handlePhotoItemDragOver = (
  event: DragEvent,
  uploadId: number,
  index: number,
) => {
  event.preventDefault();
  event.stopPropagation();
  if (draggingPhoto.value && draggingPhoto.value.uploadId === uploadId) {
    // Mettre à jour l'index de drop même si on survole une autre photo
    dragOverPhotoIndex.value = { uploadId, index };
  }
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "move";
  }
};

const handlePhotoItemDragLeave = () => {
  // Ne pas supprimer l'index si on est toujours dans la zone de drop
  // On laisse handlePhotoDrop gérer la réinitialisation
};

// Gérer le drop directement sur une photo
const handlePhotoDropOnItem = async (
  event: DragEvent,
  upload: PhotoUpload,
  dropIndex: number,
) => {
  event.preventDefault();
  event.stopPropagation();

  if (
    !canEditUpload(upload) ||
    !draggingPhoto.value ||
    draggingPhoto.value.uploadId !== upload.id
  ) {
    dragOverPhotoIndex.value = null;
    draggingPhoto.value = null;
    return;
  }

  const dragIndex = draggingPhoto.value.index;

  if (dragIndex === dropIndex) {
    dragOverPhotoIndex.value = null;
    draggingPhoto.value = null;
    return;
  }

  // Utiliser directement l'index de la photo sur laquelle on a déposé
  await performPhotoReorder(upload, dragIndex, dropIndex);
};

const handlePhotoDrop = async (event: DragEvent, upload: PhotoUpload) => {
  event.preventDefault();
  event.stopPropagation();

  if (
    !canEditUpload(upload) ||
    !draggingPhoto.value ||
    draggingPhoto.value.uploadId !== upload.id
  ) {
    dragOverPhotoIndex.value = null;
    draggingPhoto.value = null;
    return;
  }

  // Utiliser l'index de drop si disponible, sinon utiliser l'index de drag (déplacement à la même position = pas de changement)
  const dropIndex =
    dragOverPhotoIndex.value?.index ?? draggingPhoto.value.index;
  const dragIndex = draggingPhoto.value.index;

  if (dragIndex === dropIndex) {
    dragOverPhotoIndex.value = null;
    draggingPhoto.value = null;
    return;
  }

  await performPhotoReorder(upload, dragIndex, dropIndex);
};

// Fonction centralisée pour effectuer la réorganisation
const performPhotoReorder = async (
  upload: PhotoUpload,
  dragIndex: number,
  dropIndex: number,
) => {
  // Créer le nouvel ordre des photos
  const newOrder = [...upload.images];
  const [movedPhoto] = newOrder.splice(dragIndex, 1);
  newOrder.splice(dropIndex, 0, movedPhoto);

  // Extraire les positions actuelles de chaque photo dans l'ordre actuel
  const currentPositions = upload.images.map((img) =>
    getPhotoPosition(img, upload.id),
  );

  // Le backend prend les fichiers triés par position actuelle (c, z-1, z-2, etc.)
  // et les renomme selon l'ordre du tableau de positions qu'on envoie.
  // Donc on doit créer un tableau qui dit : "quelle position actuelle va à quelle nouvelle position"

  // Créer un mapping : pour chaque photo dans le nouvel ordre, trouver sa position actuelle
  const currentPositionsInNewOrder: string[] = [];
  for (let i = 0; i < newOrder.length; i++) {
    const photoUrl = newOrder[i];
    const oldIndex = upload.images.indexOf(photoUrl);
    if (oldIndex !== -1 && currentPositions[oldIndex]) {
      currentPositionsInNewOrder.push(currentPositions[oldIndex]);
    } else {
      // Fallback si on ne trouve pas la position
      currentPositionsInNewOrder.push(i === 0 ? "c" : `z-${i}`);
    }
  }

  // Le backend prend les fichiers triés par position actuelle (c, z-1, z-2, etc.)
  // et les renomme selon l'ordre du tableau qu'on envoie.
  // Donc si on envoie ['z-2', 'c', 'z-1'], il va :
  // - prendre le fichier c actuel et le renommer en z-2
  // - prendre le fichier z-1 actuel et le renommer en c
  // - prendre le fichier z-2 actuel et le renommer en z-1
  // On doit donc envoyer les positions actuelles dans l'ordre où on veut qu'elles soient réorganisées
  dragOverPhotoIndex.value = null;
  draggingPhoto.value = null;

  // Appeler l'API : on envoie les positions actuelles dans le nouvel ordre
  // Le backend va les renommer en c, z-1, z-2, etc. dans cet ordre
  await handleReorderPhotos(upload, currentPositionsInNewOrder);
};

const handleReorderPhotos = async (
  upload: PhotoUpload,
  positions: string[],
) => {
  if (!canEditUpload(upload)) {
    return;
  }

  isReorderingPhotos.value.add(upload.id);

  try {
    // L'API attend les positions actuelles dans l'ordre où elles doivent être réorganisées
    // Le backend va les renommer en c, z-1, z-2, etc. dans cet ordre
    const response = await reorderProductPhotos(upload.id, positions);

    if (response?.success) {
      setPhotoCacheBuster(upload.id);
      // Forcer le rechargement avec un petit délai pour s'assurer que les fichiers sont bien renommés
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Recharger les photos depuis le serveur
      await refreshUploadPhotos(upload);

      upload.nbPhotos = response.nbPhotos ?? upload.images.length;
      success("Photos réorganisées avec succès");
    } else {
      showError(
        response?.message || "Erreur lors de la réorganisation des photos",
      );
    }
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "Erreur lors de la réorganisation des photos";
    showError(message);
    console.error("Erreur réorganisation photos:", error);
  } finally {
    isReorderingPhotos.value.delete(upload.id);
  }
};

const isDraggingPhoto = (uploadId: number, index: number): boolean => {
  return (
    draggingPhoto.value?.uploadId === uploadId &&
    draggingPhoto.value?.index === index
  );
};

const isDragOverPhoto = (uploadId: number, index: number): boolean => {
  return (
    dragOverPhotoIndex.value?.uploadId === uploadId &&
    dragOverPhotoIndex.value?.index === index
  );
};

const isPhotoOverlayActive = (uploadId: number, index: number): boolean => {
  return (
    activePhotoOverlay.value?.uploadId === uploadId &&
    activePhotoOverlay.value?.index === index
  );
};

const photoScrollRefs = new Map<number, Element>();

const setPhotoScrollRef = (uploadId: number, el: any) => {
  const element = el instanceof Element ? el : el?.$el ?? null;

  if (element) {
    photoScrollRefs.set(uploadId, element);
  } else {
    photoScrollRefs.delete(uploadId);
  }
};

const scrollPhotos = (uploadId: number, direction: "left" | "right") => {
  const el = photoScrollRefs.get(uploadId) as HTMLElement | undefined;
  if (!el) return;
  const amount = 160;
  el.scrollBy({
    left: direction === "right" ? amount : -amount,
    behavior: "smooth",
  });
};

const togglePhotoOverlay = (uploadId: number, index: number) => {
  // Mobile/tablet only: on desktop (tablet breakpoint = 900px+) keep hover behavior
  if (typeof window !== "undefined" && window.matchMedia) {
    const isDesktop = window.matchMedia("(min-width: 900px)").matches;
    if (isDesktop) {
      return;
    }
  }

  if (isDraggingPhoto(uploadId, index)) {
    return;
  }

  activePhotoOverlay.value = isPhotoOverlayActive(uploadId, index)
    ? null
    : { uploadId, index };
};

const refreshUploadPhotos = async (upload: PhotoUpload) => {
  // Forcer un rechargement complet des photos
  await fetchProductPhotos(upload);
  // Forcer la réactivité de Vue en réassignant
  upload.images = [...upload.images];
};

const handleDeletePhoto = (upload: PhotoUpload, photoUrl: string) => {
  if (!canEditUpload(upload)) {
    return;
  }

  if (isMainPhoto(photoUrl, upload.id) && upload.premel !== "1") {
    return;
  }

  photoPendingDeletion.value = { upload, photoUrl };
  showDeletePhotoModal.value = true;
};

const confirmDeletePhoto = async () => {
  if (!photoPendingDeletion.value) {
    return;
  }

  const { upload, photoUrl } = photoPendingDeletion.value;
  const filename = getPhotoFilename(photoUrl);

  if (!filename) {
    showError("Impossible de déterminer le fichier à supprimer");
    showDeletePhotoModal.value = false;
    photoPendingDeletion.value = null;
    return;
  }

  const actionKey = `${upload.id}:${filename}:delete`;
  setPhotoActionState(actionKey, true);

  try {
    const response = await deleteProductPhoto(upload.id, filename);
    if (response?.success) {
      setPhotoCacheBuster(upload.id);
      await refreshUploadPhotos(upload);
      upload.nbPhotos = response.nbPhotos ?? upload.images.length;
      success("Photo supprimée");
    } else {
      showError(
        response?.message || "Erreur lors de la suppression de la photo",
      );
    }
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "Erreur lors de la suppression de la photo";
    showError(message);
  } finally {
    setPhotoActionState(actionKey, false);
    showDeletePhotoModal.value = false;
    photoPendingDeletion.value = null;
  }
};

const mapProduitToCatalogue = (p: Produit): ProduitCatalogue => {
  const stockRenseigne = Boolean((p as any).stock_renseigne);
  const raw = p.stock_value;
  const num = typeof raw === "number" ? raw : Number(raw);
  const stockValue =
    stockRenseigne && raw != null && !isNaN(num) ? num : undefined;

  let statut: "en-vente" | "hors-ligne" | "rupture" | "supprime";
  if ((p as any).supprimer) statut = "supprime";
  else if (!p.visible) statut = "hors-ligne";
  else if (stockRenseigne && stockValue === 0) statut = "rupture";
  else statut = "en-vente";

  return {
    id: p.id_produit,
    date: p.date_produit ?? "",
    reference: p.reference ?? "",
    reference_base: (p as any).reference_base ?? "",
    marque: p.marque ?? "",
    marqueId: p.id_vendeur_marque,
    collection: p.collection ?? "",
    collectionId: p.id_collection != null ? Number(p.id_collection) : undefined,
    ...(p.id_collection != null
      ? { id_collection: Number(p.id_collection) }
      : {}),
    id_provenance:
      (p as any).id_provenance != null
        ? Number((p as any).id_provenance)
        : null,
    compositionsRaw: (p as any).compositions ?? null,
    prix: p.prix ?? 0,
    promotion: p.promotion != null ? Number(p.promotion) : undefined,
    poids: p.poids ?? 0,
    couleur: p.couleur ?? "",
    colorIds: [],
    liaison: String(p.liaison ?? ""),
    venduPar: p.vendu_par ?? "",
    pack: p.id_pack ?? 0,
    packId: p.id_pack ?? 0,
    declinaison: p.id_declinaison ?? 0,
    declinaisonId: p.id_declinaison ?? 0,
    visible: p.visible ?? false,
    statut,
    image: "",
    id_produit: p.id_produit,
    id_vendeur: Number((p as any).id_vendeur ?? 0),
    main: p.main ?? false,
    prixReduit: p.prixReduit != null ? Number(p.prixReduit) : null,
    stock_value: stockValue,
    stock_renseigne: stockRenseigne,
    categories: p.categorie ? [p.categorie] : [],
    catLevel1Id: undefined,
    catLevel2Id: undefined,
    catLevel3Id: p.id_categorie,
    premel: (p as any).premel ?? "0",
    nb_photos: (p as any).nb_photos ?? 0,
    id_shooting: (p as any).id_shooting ?? null,
  };
};

const openProductSheetFromUpload = async (upload: PhotoUpload) => {
  const idVendeur = authStore.user?.id_vendeur ?? authStore.user?.id;
  if (!idVendeur) {
    showError("Impossible de récupérer le vendeur.");
    return;
  }

  try {
    const page = await getProductsPage({
      id_vendeur: Number(idVendeur),
      reference: upload.reference || "",
      take: 200,
      skip: 0,
      orderBy: "reference",
      orderDir: "ASC",
      includePremel: true,
    } as any);

    const fullProduct = page.items?.find(
      (item) => item.id_produit === upload.id,
    );
    if (!fullProduct) {
      showError("Produit introuvable pour ouvrir la fiche.");
      return;
    }

    const mapped = mapProduitToCatalogue(fullProduct);
    activeProductForSheet.value = {
      ...mapped,
      images: upload.images || [],
      image: upload.images[0] || "",
    } as unknown as ProduitCatalogue;
    ficheModalVisible.value = true;
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "Erreur lors de l'ouverture de la fiche.";
    showError(message);
  }
};

const handleSheetVisibleUpdate = async (visible: boolean) => {
  ficheModalVisible.value = visible;
  if (!visible) {
    if (pendingSheetSuccessMessage.value) {
      success(pendingSheetSuccessMessage.value);
      pendingSheetSuccessMessage.value = null;
    }
    activeProductForSheet.value = null;
    await loadUploads();
  }
};

const handleSaveProductSheet = async (payload: ProduitCatalogue) => {
  const productReference = payload?.reference || "produit";
  const message = `Fiche ${productReference} enregistrée avec succès.`;
  pendingSheetSuccessMessage.value = message;
  // Affichage immédiat (léger délai pour éviter d'être masqué par la modal)
  window.setTimeout(() => {
    success(message);
  }, 120);
  await loadUploads();
};

const handlePendingCover = (payload: {
  productId: number;
  coverUrl: string;
}) => {
  void payload;
  // Pas d'état local à conserver dans cette vue.
};

const handleRotatePhoto = async (upload: PhotoUpload, photoUrl: string) => {
  if (!canEditUpload(upload)) {
    return;
  }

  const filename = getPhotoFilename(photoUrl);
  if (!filename) {
    showError("Impossible de déterminer le fichier à pivoter");
    return;
  }

  const actionKey = `${upload.id}:${filename}:rotate`;
  setPhotoActionState(actionKey, true);

  try {
    const response = await rotateProductPhoto(upload.id, filename, "clockwise");
    if (response?.success) {
      setPhotoCacheBuster(upload.id);
      await refreshUploadPhotos(upload);
    } else {
      showError(response?.message || "Erreur lors de la rotation de la photo");
    }
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "Erreur lors de la rotation de la photo";
    showError(message);
  } finally {
    setPhotoActionState(actionKey, false);
  }
};

const handleSetAsCover = async (upload: PhotoUpload, photoUrl: string) => {
  if (!canEditUpload(upload)) {
    return;
  }

  // Vérifier que ce n'est pas déjà la photo de couverture
  if (isMainPhoto(photoUrl, upload.id)) {
    return;
  }

  const filename = getPhotoFilename(photoUrl);
  if (!filename) {
    showError("Impossible de déterminer le fichier");
    return;
  }

  const actionKey = `${upload.id}:${filename}:setCover`;
  setPhotoActionState(actionKey, true);

  try {
    // Trouver l'index de la photo à passer en couverture et de la photo c actuelle
    const selectedPhotoIndex = upload.images.indexOf(photoUrl);
    const currentCoverIndex = upload.images.findIndex((img) =>
      isMainPhoto(img, upload.id),
    );

    if (selectedPhotoIndex === -1) {
      showError("Photo introuvable");
      return;
    }

    if (currentCoverIndex === -1) {
      showError("Photo de couverture actuelle introuvable");
      return;
    }

    // Extraire les positions actuelles
    const currentPositions = upload.images.map((img) =>
      getPhotoPosition(img, upload.id),
    );

    // Le backend prend les fichiers triés par position actuelle (c, z-1, z-2, etc.)
    // et les renomme selon l'ordre du tableau qu'on envoie.
    // Pour intervertir : on veut que la photo z devienne c, et l'ancienne c prenne la position de z

    const selectedPosition = currentPositions[selectedPhotoIndex]; // Ex: 'z-2'
    const coverPosition = currentPositions[currentCoverIndex]; // Ex: 'c'

    // Créer un tableau de positions dans l'ordre trié actuel (c, z-1, z-2, ...)
    // Le backend va prendre les fichiers dans cet ordre et les renommer selon ce tableau
    const sortedPositions = [...currentPositions].sort((a, b) => {
      if (a === "c") return -1;
      if (b === "c") return 1;
      const numA = parseInt(a.replace("z-", "")) || 0;
      const numB = parseInt(b.replace("z-", "")) || 0;
      return numA - numB;
    });

    // Créer le tableau final : intervertir c et la position sélectionnée
    const positionsInNewOrder: string[] = sortedPositions.map((pos) => {
      if (pos === coverPosition) {
        // L'ancienne c devient la position de la photo z sélectionnée
        return selectedPosition;
      } else if (pos === selectedPosition) {
        // La photo z sélectionnée devient c
        return coverPosition;
      } else {
        // Les autres gardent leur position
        return pos;
      }
    });

    // Appeler l'API pour réorganiser
    const response = await reorderProductPhotos(upload.id, positionsInNewOrder);

    if (response?.success) {
      setPhotoCacheBuster(upload.id);
      await new Promise((resolve) => setTimeout(resolve, 300));
      await refreshUploadPhotos(upload);
      upload.nbPhotos = response.nbPhotos ?? upload.images.length;
      success("Photo de couverture mise à jour");
    } else {
      showError(
        response?.message ||
          "Erreur lors de la mise à jour de la photo de couverture",
      );
    }
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "Erreur lors de la mise à jour de la photo de couverture";
    showError(message);
  } finally {
    setPhotoActionState(actionKey, false);
  }
};

const openDeleteModal = (upload: PhotoUpload) => {
  uploadPendingDeletion.value = upload;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  if (isDeletingUpload.value) {
    return;
  }
  showDeleteModal.value = false;
  uploadPendingDeletion.value = null;
};

const removeUploadLocally = (uploadId: number) => {
  setUploadingProduct(uploadId, false);
  uploads.value = uploads.value.filter((upload) => upload.id !== uploadId);
  applyFiltersAndSort();
};

const confirmDeleteUpload = async () => {
  if (!uploadPendingDeletion.value) {
    closeDeleteModal();
    return;
  }

  try {
    isDeletingUpload.value = true;
    const uploadToDelete = uploadPendingDeletion.value;

    await deleteProduct(uploadToDelete.id);

    await loadUploads();
    success(`Upload ${uploadToDelete.reference} supprimé`);
    isDeletingUpload.value = false;
    closeDeleteModal();
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : "Une erreur est survenue lors de la suppression";
    showError(errorMessage);
    isDeletingUpload.value = false;
  } finally {
    if (isDeletingUpload.value) {
      isDeletingUpload.value = false;
    }
  }
};

// Méthodes de gestion des données
const loadUploads = async () => {
  const requestId = ++loadUploadsRequestSeq.value;
  isLoading.value = true;
  try {
    const vendorId = authStore.user?.id_vendeur;
    if (!vendorId) {
      return;
    }
    uploadingProductIds.value = [];
    const filters = {
      shootingIds: shootingIdsFromUrl.value,
      reference: currentFilters.value.reference || undefined,
      idShooting: currentFilters.value.idShooting || undefined,
      premelFilter: currentFilters.value.premelFilter || undefined,
      page: currentPage.value,
      limit: itemsPerPage.value,
      sortBy: currentFilters.value.tri || "date-desc",
    };
    const [response, brouillonResponse] = await Promise.all([
      getUploadProducts(vendorId, filters),
      getUploadProducts(vendorId, {
        shootingIds: shootingIdsFromUrl.value,
        premelFilter: "brouillon",
        page: 1,
        limit: 1,
      }),
    ]);
    if (requestId !== loadUploadsRequestSeq.value) {
      return;
    }

    if (!response || !response.items) {
      uploads.value = [];
      filteredUploads.value = [];
      totalItems.value = 0;
      brouillonTotalCount.value = 0;
      uploadingProductIds.value = [];
      return;
    }

    totalItems.value = response.total || 0;
    brouillonTotalCount.value = brouillonResponse?.total ?? 0;

    uploads.value = response.items.map(mapUploadProductApiItemToPhotoUpload);

    await fetchProductPhotosBatch(uploads.value);

    // Avec pagination côté serveur, pas de filtrage côté client
    filteredUploads.value = uploads.value;
  } catch (error) {
    if (requestId !== loadUploadsRequestSeq.value) {
      return;
    }
    uploads.value = [];
    filteredUploads.value = [];
    brouillonTotalCount.value = 0;
    uploadingProductIds.value = [];
  } finally {
    if (requestId === loadUploadsRequestSeq.value) {
      isLoading.value = false;
    }
  }
};

const handleShowAll = () => {
  window.location.href = "/catalog/uploadphoto";
};

const publishDraftReferences = computed(() =>
  publishDraftCandidates.value.map((item) => item.reference),
);

const publishDraftConfirmMessage = computed(() => {
  return `Les produits brouillons ci-dessous (avec au moins une photo) vont être mis en ligne .`;
});

const closePublishDraftConfirmModal = (force = false) => {
  if (isPublishingDraftProducts.value && !force) {
    return;
  }
  showPublishDraftConfirmModal.value = false;
  publishDraftCandidates.value = [];
};

const closeUnifiedPublishModal = (force = false) => {
  if (isChoosingMain.value && !force) {
    return;
  }
  chooseMainModalVisible.value = false;
  chooseMainGroups.value = [];
  publishDraftCandidates.value = [];
  publishStep.value = 1;
};

// Récupère TOUS les brouillons de toutes les pages (la liste affichée est
// paginée côté serveur). On boucle sur toutes les pages pour pouvoir mettre
// en ligne l'intégralité des brouillons, pas seulement la page courante.
const loadAllBrouillonUploads = async (
  vendorId: number,
): Promise<PhotoUpload[]> => {
  const pageSize = 200;
  const first = await getUploadProducts(vendorId, {
    shootingIds: shootingIdsFromUrl.value,
    premelFilter: "brouillon",
    page: 1,
    limit: pageSize,
  });
  let items = [...(first.items ?? [])];
  const totalPages = first.totalPages ?? 1;
  for (let p = 2; p <= totalPages; p++) {
    const r = await getUploadProducts(vendorId, {
      shootingIds: shootingIdsFromUrl.value,
      premelFilter: "brouillon",
      page: p,
      limit: pageSize,
    });
    items = items.concat(r.items ?? []);
  }

  return items.map(mapUploadProductApiItemToPhotoUpload);
};

const handleGoToMel = async () => {
  if (isPreparingPublishDraftModal.value || isPublishingDraftProducts.value) {
    return;
  }
  isPreparingPublishDraftModal.value = true;
  try {
    const vendorId = authStore.user?.id_vendeur;
    if (!vendorId) {
      showError("Impossible de récupérer l'identifiant vendeur.");
      return;
    }

    const hasPhoto = (u: PhotoUpload) => (u.nbPhotos ?? u.images.length) > 0;

    // Récupérer TOUS les brouillons (toutes les pages), pas seulement la page
    // courante, afin de mettre en ligne l'intégralité des brouillons avec photo.
    const allItems = await loadAllBrouillonUploads(vendorId);

    // Détecter les groupes dont la couleur principale (brouillon) n'a PAS de
    // photo mais qui possèdent des variantes (brouillon) AVEC photo. Dans ce
    // cas, on demande à l'utilisateur de choisir une nouvelle principale avant
    // la mise en ligne. La principale sans photo n'est jamais mise en ligne.
    const groups: Record<string, PhotoUpload[]> = {};
    for (const u of allItems) {
      const key = u.referenceBase ?? u.reference;
      (groups[key] ||= []).push(u);
    }

    const needChoice: typeof chooseMainGroups.value = [];
    const needChoiceIds = new Set<number>();
    for (const list of Object.values(groups)) {
      const main = list.find((p) => p.isMain && p.premel === "1");
      if (main && !hasPhoto(main)) {
        const candidates = list.filter(
          (p) => p.id !== main.id && p.premel === "1" && hasPhoto(p),
        );
        if (candidates.length > 0) {
          needChoice.push({
            oldMainId: main.id,
            oldMainRef: main.reference || `Produit #${main.id}`,
            selectedId: candidates[0].id,
            candidates,
          });
          candidates.forEach((c) => needChoiceIds.add(c.id));
          needChoiceIds.add(main.id);
        }
      }
    }

    // Produits normaux à mettre en ligne : brouillon AVEC photo qui
    // n'appartiennent à aucun groupe nécessitant un choix de principale.
    const normalProducts = allItems.filter(
      (u) => u.premel === "1" && hasPhoto(u) && !needChoiceIds.has(u.id),
    );

    if (needChoice.length === 0 && normalProducts.length === 0) {
      showError(
        "Aucun produit brouillon avec au moins une image n'a ete trouvée.",
      );
      return;
    }

    // Charger les photos des candidats à afficher dans l'étape 1.
    const candidatesToLoad: PhotoUpload[] = [];
    for (const g of needChoice) {
      candidatesToLoad.push(...g.candidates);
    }
    if (candidatesToLoad.length > 0) {
      await fetchProductPhotosBatch(candidatesToLoad);
    }

    chooseMainGroups.value = needChoice;
    publishDraftCandidates.value = normalProducts.map((upload) => ({
      id: upload.id,
      reference: upload.reference || `Produit #${upload.id}`,
    }));

    // Étape 1 si des choix de principale sont requis, sinon directement étape 2.
    publishStep.value = needChoice.length > 0 ? 1 : 2;
    chooseMainModalVisible.value = true;
  } finally {
    isPreparingPublishDraftModal.value = false;
  }
};

// Confirmer le choix des nouvelles principales (pour les groupes dont la
// principale n'avait pas de photo) puis mettre en ligne les brouillons AVEC photo.
const confirmChooseMainAndPublish = async () => {
  if (!chooseMainGroups.value.length && !publishDraftCandidates.value.length) {
    closeUnifiedPublishModal(true);
    return;
  }
  const vendorId = authStore.user?.id ?? authStore.user?.id_vendeur;
  if (!vendorId) {
    showError("Impossible de récupérer l'identifiant vendeur.");
    return;
  }
  isChoosingMain.value = true;
  try {
    const hasPhoto = (u: PhotoUpload) => (u.nbPhotos ?? u.images.length) > 0;

    // 1) Pour chaque groupe, la variante choisie (avec photo) devient la
    //    nouvelle principale ; l'ancienne principale (sans photo) est
    //    rétrogradée en variante et reste en brouillon.
    for (const g of chooseMainGroups.value) {
      await toggleMainProduct(g.selectedId);
    }

    // 2) Mettre en ligne UNIQUEMENT les produits des groupes traités dans le
    //    modal : seuls les brouillons AVEC photo de ces groupes sont publiés
    //    (ceux sans photo, dont les anciennes principales, restent en brouillon).
    //    Les autres groupes/produits non sélectionnés ne sont pas touchés.
    const productIdSet = new Set<number>();
    for (const g of chooseMainGroups.value) {
      for (const c of g.candidates) {
        if (c.premel === "1" && hasPhoto(c)) {
          productIdSet.add(c.id);
        }
      }
      // S'assurer que la nouvelle principale choisie est incluse.
      productIdSet.add(g.selectedId);
    }
    // 3) Ajouter les produits normaux (étape 2) à la même mise en ligne.
    for (const item of publishDraftCandidates.value) {
      productIdSet.add(item.id);
    }
    const productIds = Array.from(productIdSet);
    if (productIds.length === 0) {
      closeUnifiedPublishModal(true);
      return;
    }

    const publishedCount = await publishBrouillonBulk(productIds, vendorId);
    success(`${publishedCount} produit(s) mis en ligne avec succès.`);
    await loadUploads();
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "Erreur lors de la mise en ligne des produits";
    showError(message);
  } finally {
    isChoosingMain.value = false;
    closeUnifiedPublishModal(true);
  }
};

const confirmPublishDraftProducts = async () => {
  if (!publishDraftCandidates.value.length) {
    closePublishDraftConfirmModal();
    return;
  }

  const vendorId = authStore.user?.id ?? authStore.user?.id_vendeur;
  if (!vendorId) {
    showError("Impossible de récupérer l'identifiant vendeur.");
    return;
  }

  isPublishingDraftProducts.value = true;
  try {
    const productIds = publishDraftCandidates.value.map((item) => item.id);
    const publishedCount = await publishBrouillonBulk(productIds, vendorId);
    success(`${publishedCount} produit(s) mis en ligne avec succès.`);
    closePublishDraftConfirmModal(true);
    await loadUploads();
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "Erreur lors de la mise en ligne des produits";
    showError(message);
  } finally {
    isPublishingDraftProducts.value = false;
  }
};

const handleFilter = (filters: UploadProductFiltersData) => {
  const previousFilters = { ...currentFilters.value };
  uploadPhotoStore.setFilters(filters);

  const referenceChanged = filters.reference !== previousFilters.reference;
  const shootingChanged = filters.idShooting !== previousFilters.idShooting;
  const triChanged = filters.tri !== previousFilters.tri;
  const categorieChanged = filters.categorie !== previousFilters.categorie;
  const sousCategorieChanged =
    filters.sousCategorie !== previousFilters.sousCategorie;
  const sousSousCategorieChanged =
    filters.sousSousCategorie !== previousFilters.sousSousCategorie;
  const hasPhotosChanged =
    (filters.hasPhotos || "") !== (previousFilters.hasPhotos || "");
  const premelFilterChanged =
    (filters.premelFilter || "") !== (previousFilters.premelFilter || "");

  if (
    referenceChanged ||
    shootingChanged ||
    triChanged ||
    premelFilterChanged
  ) {
    uploadPhotoStore.setPage(1);
    loadUploads();
    return;
  }

  if (
    categorieChanged ||
    sousCategorieChanged ||
    sousSousCategorieChanged ||
    hasPhotosChanged
  ) {
    applyFiltersAndSort();
  }
};

const canEditUpload = (upload: PhotoUpload) => {
  void upload;
  // Comme l'ancien PHP: premel = "include" → pas de restriction sur premel
  // Tous les produits peuvent recevoir des photos
  return true;
};

function triggerZipInput() {
  zipFileInputRef.value?.click();
}

function handleZipFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file && file.name.toLowerCase().endsWith(".zip")) {
    selectedZipFile.value = file;
    zipErrorMessage.value = "";
    zipErrors.value = [];
    showZipErrors.value = false;
  } else if (file) {
    selectedZipFile.value = null;
    zipErrorMessage.value = "Veuillez sélectionner un fichier .zip";
    showError("Veuillez sélectionner un fichier .zip");
  }
  target.value = "";
}

function handleZipDrop(event: DragEvent) {
  zipDragOver.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file && file.name.toLowerCase().endsWith(".zip")) {
    selectedZipFile.value = file;
    zipErrorMessage.value = "";
    zipErrors.value = [];
    showZipErrors.value = false;
  } else if (file) {
    zipErrorMessage.value = "Veuillez déposer un fichier .zip";
    selectedZipFile.value = null;
    showError("Veuillez déposer un fichier .zip");
  }
}

async function submitZipUpload() {
  if (!selectedZipFile.value || isZipUploading.value) return;
  const file = selectedZipFile.value;
  const maxSize = 100 * 1024 * 1024;
  if (file.size > maxSize) {
    zipErrorMessage.value = `Fichier trop volumineux (max 100 Mo). Reçu : ${Math.round(
      file.size / 1024 / 1024,
    )} Mo.`;
    showError(zipErrorMessage.value);
    return;
  }
  isZipUploading.value = true;
  zipErrorMessage.value = "";
  zipErrors.value = [];
  try {
    const result = await uploadProductPhotosBulkZip(file);
    if (result.processed && result.processed > 0) {
      success(result.message);
      selectedZipFile.value = null;
      await loadUploads();
    }
    if (result.errors?.length) {
      zipErrors.value = [...new Set(result.errors)];
      showZipErrors.value = true;
      if (!result.processed || result.processed === 0) {
        zipErrorMessage.value =
          result.message ||
          "Aucun produit traité. Vérifiez les erreurs ci-dessous.";
        showError(zipErrorMessage.value);
      } else {
        showError(
          "Certaines photos n’ont pas pu être importées (voir le détail).",
        );
      }
    }
    if (!result.success && result.message && !result.errors?.length) {
      zipErrorMessage.value = result.message;
      showError(result.message);
    }
  } catch (e) {
    zipErrorMessage.value =
      e instanceof Error ? e.message : "Erreur lors du traitement du ZIP";
    showError(zipErrorMessage.value);
  } finally {
    isZipUploading.value = false;
  }
}

// Initialisation
onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: "auth" });
    return;
  }
  await ensureReferenceDataLoaded();
  // Charger l'arbre des catégories pour le filtre à 3 niveaux
  categoriesTree.value = await getCategoriesTree("fr");
  const pageReset = syncShootingContext();
  if (!pageReset) {
    await loadUploads();
  }
});

// Recharger les données quand la pagination ou les filtres changent
watch(currentPage, async () => {
  uploadPhotoStore.persistNow();
  await loadUploads();
});
watch(itemsPerPage, async () => {
  uploadPhotoStore.persistNow();
  // Reset to first page when changing items per page
  currentPage.value = 1;
  await loadUploads();
});
watch(
  currentFilters,
  async (newFilters, oldFilters) => {
    // Reset to first page when filters change
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    }
    uploadPhotoStore.persistNow();

    // Seuls reference et idShooting nécessitent un rechargement serveur
    const referenceChanged = newFilters.reference !== oldFilters?.reference;
    const shootingChanged = newFilters.idShooting !== oldFilters?.idShooting;

    if (referenceChanged || shootingChanged) {
      await loadUploads();
    } else {
      // Pour les autres filtres (tri, catégories, hasPhotos), appliquer côté client
      applyFiltersAndSort();
    }
  },
  { deep: true },
);

watch(shootingIdsFromUrl, async (newVal, oldVal) => {
  if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
    const pageReset = syncShootingContext();
    if (!pageReset) {
      await loadUploads();
    }
  }
});
</script>

<style scoped>
.desktop-upload-actions {
  display: flex;
}

.pagination-inline :deep(.flex-col) {
  flex-direction: row !important;
}

@media (max-width: 1024px) {
  .desktop-upload-actions,
  .upload-mode-grid {
    display: none;
  }
}
</style>
